import { z } from 'zod';
import { MailboxProviderSchema } from './mailbox.schemas';

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const CalendarConnectionStateSchema = z.enum(['NOT_CONNECTED', 'CONNECTED', 'EXPIRED']);
export type CalendarConnectionState = z.infer<typeof CalendarConnectionStateSchema>;

export const CalendarAppointmentTypeSchema = z.enum([
  'VIEWING',
  'ON_SITE',
  'OWNER_MEETING',
  'VALUATION',
  'NOTARY',
  'HANDOVER',
  'PHONE',
  'ONLINE_MEETING',
  'OTHER',
]);
export type CalendarAppointmentType = z.infer<typeof CalendarAppointmentTypeSchema>;

export const AppointmentStatusSchema = z.enum(['CONFIRMED', 'CANCELLED']);
export type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

export const AttendeeResponseSchema = z.enum(['NEEDS_ACTION', 'ACCEPTED', 'DECLINED', 'TENTATIVE']);
export type AttendeeResponse = z.infer<typeof AttendeeResponseSchema>;

export const GetCalendarConnectionPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ConferenceUnavailableReasonSchema = z.enum(['PERSONAL_MICROSOFT_ACCOUNT']);
export type ConferenceUnavailableReason = z.infer<typeof ConferenceUnavailableReasonSchema>;

export const CalendarConnectionSchema = z.object({
  state: CalendarConnectionStateSchema,
  accountEmail: z.string().nullable(),
  // Null exactly when NOT_CONNECTED. Feeds the toolbar chip's logo and the Teams wording.
  provider: MailboxProviderSchema.nullable(),
  canCreateConferences: z.boolean(),
  unavailableReason: z
    .enum(['IMAP_MAILBOX'])
    .nullable()
    .describe(
      'Why no calendar can be connected for this member: `IMAP_MAILBOX` when their mailbox is connected over IMAP, which carries no calendar (IMM-1173.6). Null otherwise.',
    ),
  // Off for a provider that keeps the meeting online once created (Outlook): the form says the removal is local.
  removesConferencesAtProvider: z.boolean(),
  // Non-null only for a personal Microsoft account: the form shows the "Einwahllink" hint instead of hiding the section.
  conferenceUnavailableReason: ConferenceUnavailableReasonSchema.nullable(),
});
export type CalendarConnection = z.infer<typeof CalendarConnectionSchema>;

export const SyncCalendarPathParamsSchema = WorkspaceIdPathParamsSchema;
export const SyncCalendarResponseSchema = z.object({
  appliedChangeCount: z.number().int().nonnegative(),
  queuedChangeCount: z.number().int().nonnegative(),
  skipped: z.boolean(),
});
export type SyncCalendarResponse = z.infer<typeof SyncCalendarResponseSchema>;

export const MAX_CALENDAR_WINDOW_DAYS = 62;
const MAX_CALENDAR_WINDOW_MS = MAX_CALENDAR_WINDOW_DAYS * 24 * 60 * 60 * 1000;

export const ListCalendarEventsPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CalendarEventsQuerySchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine((window) => new Date(window.to) > new Date(window.from), { path: ['to'] })
  .refine((window) => new Date(window.to).getTime() - new Date(window.from).getTime() <= MAX_CALENDAR_WINDOW_MS, {
    path: ['to'],
  });
export type CalendarEventsQuery = z.infer<typeof CalendarEventsQuerySchema>;

export const AppointmentAttendeeSchema = z.object({
  email: z.string(),
  displayName: z.string().nullable(),
  response: AttendeeResponseSchema,
});
export type AppointmentAttendee = z.infer<typeof AppointmentAttendeeSchema>;

export const AppointmentLinkRecordTypeSchema = z.enum(['PROPERTY', 'CONTACT', 'DEAL', 'LEAD', 'PROSPECT']);
export type AppointmentLinkRecordType = z.infer<typeof AppointmentLinkRecordTypeSchema>;

export const AppointmentLinkInputSchema = z.object({
  recordType: AppointmentLinkRecordTypeSchema,
  recordId: z.uuid(),
});
export type AppointmentLinkInput = z.infer<typeof AppointmentLinkInputSchema>;

/**
 * `label` is empty when the linked record no longer resolves. The link is still
 * returned: a later save replaces links as a set, so omitting it here would delete it.
 */
export const AppointmentLinkSchema = z.object({
  recordType: AppointmentLinkRecordTypeSchema,
  recordId: z.uuid(),
  label: z.string(),
});
export type AppointmentLink = z.infer<typeof AppointmentLinkSchema>;

export const AppointmentSchema = z.object({
  id: z.uuid(),
  type: CalendarAppointmentTypeSchema,
  title: z.string().nullable(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  isAllDay: z.boolean(),
  isPrivate: z.boolean(),
  status: AppointmentStatusSchema,
  location: z.string().nullable(),
  notes: z.string().nullable(),
  joinUrl: z.string().nullable(),
  conferenceUrl: z.string().nullable(),
  hasConflict: z.boolean(),
  attendees: z.array(AppointmentAttendeeSchema),
  links: z.array(AppointmentLinkSchema),
  canEdit: z.boolean(),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

export const CalendarEventsResponseSchema = z.object({
  appointments: z.array(AppointmentSchema),
  connectionState: CalendarConnectionStateSchema,
});
export type CalendarEventsResponse = z.infer<typeof CalendarEventsResponseSchema>;

export const isIanaZone = (zone: string): boolean => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });

    return true;
  } catch {
    return false;
  }
};

export const AttendeeEmailsSchema = z.array(z.email().max(320)).max(100);
const ConferenceInstructionSchema = z.enum(['CREATE', 'REMOVE']);

export const ConferenceWarningSchema = z.literal('CREATION_FAILED');
export type ConferenceWarning = z.infer<typeof ConferenceWarningSchema>;

export const AppointmentWriteResponseSchema = AppointmentSchema.extend({
  conferenceWarning: ConferenceWarningSchema.nullable(),
});
export type AppointmentWriteResponse = z.infer<typeof AppointmentWriteResponseSchema>;

export const CreateAppointmentPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CreateAppointmentBodySchema = z
  .object({
    type: CalendarAppointmentTypeSchema.default('OTHER'),
    title: z.string().trim().min(1).max(200),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    isAllDay: z.boolean().default(false),
    timeZone: z.string().min(1).refine(isIanaZone),
    location: z.string().trim().max(300).optional(),
    notes: z.string().trim().max(5000).optional(),
    joinUrl: z.string().trim().max(500).optional(),
    links: z.array(AppointmentLinkInputSchema).max(50).optional(),
    attendees: AttendeeEmailsSchema.optional(),
    conference: ConferenceInstructionSchema.optional(),
  })
  .refine((body) => new Date(body.endsAt) > new Date(body.startsAt), { path: ['endsAt'] });
export type CreateAppointmentBody = z.input<typeof CreateAppointmentBodySchema>;

export const UpdateAppointmentPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  appointmentId: z.uuid(),
});
export const CancelAppointmentPathParamsSchema = UpdateAppointmentPathParamsSchema;

// `null` clears a field, an absent key leaves the stored value alone. A drag sends BOTH
// bounds: accepting a lone `startsAt` would mean inventing the duration server-side.
export const UpdateAppointmentBodySchema = z
  .object({
    type: CalendarAppointmentTypeSchema.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    isAllDay: z.boolean().optional(),
    timeZone: z.string().min(1).refine(isIanaZone),
    location: z.string().trim().max(300).nullable().optional(),
    notes: z.string().trim().max(5000).nullable().optional(),
    joinUrl: z.string().trim().max(500).nullable().optional(),
    links: z.array(AppointmentLinkInputSchema).max(50).optional(),
    attendees: AttendeeEmailsSchema.optional(),
    conference: ConferenceInstructionSchema.optional(),
  })
  .refine((body) => body.startsAt === undefined || body.endsAt !== undefined, { path: ['endsAt'] })
  .refine(
    (body) =>
      body.startsAt === undefined || body.endsAt === undefined || new Date(body.endsAt) > new Date(body.startsAt),
    { path: ['endsAt'] },
  );
export type UpdateAppointmentBody = z.input<typeof UpdateAppointmentBodySchema>;

export const GetDealSuggestionsPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  dealId: z.uuid(),
});
export const DealSuggestionRecordSchema = z.object({ recordId: z.uuid(), label: z.string() });
export const DealSuggestionsResponseSchema = z.object({
  property: DealSuggestionRecordSchema.nullable(),
  contacts: z.array(DealSuggestionRecordSchema),
  contactsUnambiguous: z.boolean(),
});
export type DealSuggestionsResponse = z.infer<typeof DealSuggestionsResponseSchema>;

export const UpcomingPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  recordType: AppointmentLinkRecordTypeSchema,
  recordId: z.uuid(),
});

export const UPCOMING_APPOINTMENTS_LIMIT = 20;

export const UpcomingAppointmentSchema = z.object({
  id: z.uuid(),
  type: CalendarAppointmentTypeSchema,
  title: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  isAllDay: z.boolean(),
  status: AppointmentStatusSchema,
  location: z.string().nullable(),
  ownerName: z.string(),
});
export type UpcomingAppointment = z.infer<typeof UpcomingAppointmentSchema>;

export const UpcomingAppointmentsResponseSchema = z.object({
  appointments: z.array(UpcomingAppointmentSchema).max(UPCOMING_APPOINTMENTS_LIMIT),
});
export type UpcomingAppointmentsResponse = z.infer<typeof UpcomingAppointmentsResponseSchema>;
